import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import ConfirmModal from '../../components/ConfirmModal'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  getMisSuscripciones, getDashboardResumen, getKitsConProductos,
  pausarSuscripcion, reactivarSuscripcion, cancelarMiSuscripcion,
  cancelarPreapprovalMP, cambiarDiaSuscripcion,
} from '../../lib/portalClienteApi'
import { VENTANAS_HORARIAS, labelVentana } from '../../lib/ventanasHorarias'

const DIAS_SEMANA = [
  { v: 'lunes',     l: 'Lunes' },
  { v: 'martes',    l: 'Martes' },
  { v: 'miercoles', l: 'Miércoles' },
  { v: 'jueves',    l: 'Jueves' },
  { v: 'viernes',   l: 'Viernes' },
]

function calcEstadoEntrega(proximaFecha) {
  if (!proximaFecha) return { tipo: 'sin_entrega' }
  const ahora = new Date()
  const entrega = new Date(proximaFecha + 'T12:00:00')
  const hoy0 = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
  const entrega0 = new Date(entrega.getFullYear(), entrega.getMonth(), entrega.getDate())
  const dias = Math.round((entrega0 - hoy0) / (24 * 60 * 60 * 1000))
  const corteH = 16
  if (dias < 0) return { tipo: 'pasada', dias }
  if (dias === 0) return { tipo: 'd1_cerrado', dias, msg: 'Tu kit ya está en ruta hacia tu casa.' }
  if (dias === 1) {
    if (ahora.getHours() < corteH) {
      return { tipo: 'd1_editable', dias, deadline: '16:00 hs de hoy' }
    }
    return { tipo: 'd1_cerrado', dias, msg: 'Tu kit ya está armado y sale mañana.' }
  }
  return { tipo: 'a_tiempo', dias }
}

const ESTADO_PILL = {
  activa:    { color: '#16a34a', label: 'Activa' },
  pausada:   { color: '#f59e0b', label: 'Pausada' },
  cancelada: { color: '#ef4444', label: 'Cancelada' },
}

const TIER_BADGE = {
  essential: { bg: '#e6fafc', color: '#0f7480', label: 'Línea Essential' },
  premium:   { bg: '#fef3c7', color: '#92400e', label: 'Línea Premium'   },
  comercial: { bg: '#ede9fe', color: '#5b21b6', label: 'Línea Comercial' },
}

function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
}

function fmtFechaLarga(iso) {
  if (!iso) return '—'
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado']
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

// Regla cobro: D-1 a las 16hs, con snap a viernes si cae sábado o domingo.
// Comunicado en NuevaSuscripcion.jsx Paso 5 y Soporte.jsx FAQ.
function diaAntesDe(isoFecha) {
  if (!isoFecha) return null
  const d = new Date(isoFecha + (isoFecha.length === 10 ? 'T12:00:00' : ''))
  d.setDate(d.getDate() - 1)
  // 0=domingo, 6=sábado → retroceder hasta viernes
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() - 1)
  }
  return d.toISOString().slice(0, 10)
}

function diaSemanaLabel(d) {
  const map = { lunes: 'Lun', martes: 'Mar', miercoles: 'Mié', jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom' }
  return map[d] || d
}

// Emoji placeholder según nombre del producto (mientras no haya foto cargada)
function emojiPorNombre(nombre = '') {
  const n = nombre.toLowerCase()
  if (n.includes('papel higiénico') || n.includes('higienico')) return '🧻'
  if (n.includes('papel') || n.includes('toalla')) return '🧻'
  if (n.includes('jabón ropa') || n.includes('jabón de ropa') || n.includes('lavarropa')) return '🧺'
  if (n.includes('2-en-1') || n.includes('suavizante')) return '🧺'
  if (n.includes('lavavajillas') || n.includes('detergente')) return '🍽️'
  if (n.includes('limpiapisos') || n.includes('piso')) return '🪣'
  if (n.includes('desengrasante')) return '🍳'
  if (n.includes('esponja')) return '🧽'
  if (n.includes('vidrio')) return '🪟'
  if (n.includes('bolsa')) return '🗑️'
  if (n.includes('manos')) return '🧼'
  if (n.includes('lavandina') || n.includes('cloro')) return '🧴'
  if (n.includes('desinfectante') || n.includes('limpiador')) return '🧴'
  if (n.includes('perfume') || n.includes('ambiente')) return '🌸'
  return '🧴'
}

export default function MiPlan() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [suscripciones, setSuscripciones] = useState([])
  const [resumen, setResumen] = useState(null)
  const [kitProductos, setKitProductos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [accion, setAccion] = useState(null)  // null | 'pausar' | 'cancelar'
  const [actualizadoAviso, setActualizadoAviso] = useState(false)
  const debounceRef = useRef(null)

  // Las canceladas NO aparecen en el selector principal — van a "Historial".
  const suscActivas = suscripciones.filter(s => s.estado !== 'cancelada')
  const suscHistorial = suscripciones.filter(s => s.estado === 'cancelada')
  const [mostrarHistorial, setMostrarHistorial] = useState(false)

  // Suscripción activa: por defecto la primera de las activas, o la que venga en ?susc=ID.
  // Si el ?susc=ID apunta a una cancelada, abrimos el historial.
  const suscIdUrl = searchParams.get('susc')
  const ixActivo = (() => {
    if (suscIdUrl) {
      const ix = suscActivas.findIndex(s => s.id === suscIdUrl)
      if (ix >= 0) return ix
    }
    return 0
  })()

  // Si el deeplink apunta a una cancelada, abrimos el panel de historial automáticamente.
  useEffect(() => {
    if (!suscIdUrl) return
    const esHistorial = suscHistorial.some(s => s.id === suscIdUrl)
    if (esHistorial) setMostrarHistorial(true)
  }, [suscIdUrl, suscHistorial.length])

  function cambiarSuscripcionActiva(suscId) {
    const params = new URLSearchParams(searchParams)
    if (suscId) params.set('susc', suscId)
    else params.delete('susc')
    setSearchParams(params, { replace: true })
  }

  useEffect(() => { if (user?.id) cargar() }, [user?.id])

  // Recargar resumen + kitProductos cuando cambia la suscripción activa
  useEffect(() => {
    const sActiva = suscActivas[ixActivo]
    if (!sActiva) return
    let cancelado = false
    ;(async () => {
      const [r, kits] = await Promise.all([
        getDashboardResumen(sActiva.id),
        getKitsConProductos(),
      ])
      if (cancelado) return
      setResumen(r)
      const miKit = kits.find(k => k.codigo === sActiva.kit_actual)
      setKitProductos(miKit?.productos || [])
    })()
    return () => { cancelado = true }
  }, [ixActivo, suscActivas[ixActivo]?.id])

  // Realtime: si admin cambia composición del kit (kit_items) o un producto
  // (foto, nombre, presentación), refrescamos automáticamente.
  useEffect(() => {
    if (!user?.id) return
    const kitCodigo = suscActivas[ixActivo]?.kit_actual
    if (!kitCodigo) return

    const recargar = () => {
      // Debounce: si llegan varios cambios juntos, agrupamos
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        cargar()
        setActualizadoAviso(true)
        setTimeout(() => setActualizadoAviso(false), 4000)
      }, 600)
    }

    const ch = supabase
      .channel(`miplan:${kitCodigo}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'kit_items', filter: `kit_codigo=eq.${kitCodigo}` },
        recargar)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'productos' },
        (payload) => {
          // Solo refrescamos si el producto cambiado está en mi kit
          const skuCambiado = payload.new?.sku
          if (skuCambiado && kitProductos.some(p => p.sku === skuCambiado)) recargar()
        })
      .subscribe()

    return () => {
      clearTimeout(debounceRef.current)
      supabase.removeChannel(ch)
    }
  }, [user?.id, suscActivas[ixActivo]?.kit_actual, kitProductos.length])

  async function cargar() {
    setCargando(true)
    const ss = await getMisSuscripciones(user.id)
    setSuscripciones(ss)
    setCargando(false)
    // resumen + kitProductos los carga el useEffect que escucha ixActivo
  }

  const susc = suscActivas[ixActivo]
  const tieneSusc = !!susc
  const estado = susc?.estado || 'activa'
  const estadoInfo = ESTADO_PILL[estado] || ESTADO_PILL.activa
  const proximaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  const proximoPago = resumen?.proximoPago

  // Estado para confirm modal (reemplaza window.confirm/alert nativos)
  const [modalAccion, setModalAccion] = useState(null)
  // null | { tipo, titulo, mensaje, confirmLabel, variant, onConfirm, procesando, mensajeError }

  async function handlePausar() {
    setModalAccion({
      tipo: 'pausar',
      titulo: 'Pausar suscripción 1 mes',
      mensaje: <>No vamos a hacer entregas ni cobrarte hasta que la <strong>reactives</strong>. Podés volver cuando quieras.</>,
      confirmLabel: 'Sí, pausar 1 mes',
      procesando: false,
      onConfirm: async () => {
        setModalAccion(prev => ({ ...prev, procesando: true }))
        try {
          await pausarSuscripcion(susc.id, 'Pausa solicitada por cliente')
          setModalAccion(null)
          cargar()
        } catch (e) {
          setModalAccion({
            tipo: 'error', titulo: 'No pudimos pausar', mensaje: e.message,
            confirmLabel: 'Cerrar', cancelLabel: null,
            onConfirm: () => setModalAccion(null),
          })
        }
      },
    })
  }

  async function handleReactivar() {
    setModalAccion({
      tipo: 'reactivar',
      titulo: 'Reactivar suscripción',
      mensaje: <>Volvemos a armar y entregarte el kit en tu próxima fecha de barrio. El primer cobro va el día anterior, 16hs.</>,
      confirmLabel: 'Sí, reactivar',
      procesando: false,
      onConfirm: async () => {
        setModalAccion(prev => ({ ...prev, procesando: true }))
        try {
          await reactivarSuscripcion(susc.id)
          setModalAccion(null)
          cargar()
        } catch (e) {
          setModalAccion({
            tipo: 'error', titulo: 'No pudimos reactivar', mensaje: e.message,
            confirmLabel: 'Cerrar', cancelLabel: null,
            onConfirm: () => setModalAccion(null),
          })
        }
      },
    })
  }

  const [modalCancelar, setModalCancelar] = useState(null)
  const [modalEntrega, setModalEntrega] = useState(false)
  // null | 'confirmar' | 'procesando' | { tipo: 'resultado', resumen }

  function abrirModalCancelar() {
    setModalCancelar('confirmar')
  }

  async function ejecutarCancelacion(motivo) {
    setModalCancelar('procesando')
    try {
      const resumen = await cancelarMiSuscripcion(
        susc.id,
        motivo || 'Cancelado por cliente desde portal',
      )
      // Si había preapproval activo en MP, cancelarlo también
      if (resumen?.requiere_cancelar_mp) {
        try { await cancelarPreapprovalMP(susc.id) } catch (e) {
          console.warn('No se pudo cancelar el preapproval en MP:', e)
          // No bloqueamos: la suscripción ya está cancelada local,
          // el resumen muestra info de qué pasó.
        }
      }
      setModalCancelar({ tipo: 'resultado', resumen })
    } catch (e) {
      alert('Error al cancelar: ' + e.message)
      setModalCancelar('confirmar')
    }
  }

  return (
    <ClienteLayout title="Mi Plan">
      <div className="mp-wrap">
        <div className="mp-headline">
          <h1>Mi Plan</h1>
          <p>Tu suscripción actual y todo lo que viene en tus próximas entregas.</p>
        </div>

        {cargando ? (
          <div className="mp-loading">Cargando…</div>
        ) : !tieneSusc ? (
          <>
            <div className="mp-empty">
              <div className="mp-empty-icon">📦</div>
              <h2>{suscHistorial.length > 0 ? 'No tenés suscripciones activas' : 'Todavía no tenés un plan activo'}</h2>
              <p>
                {suscHistorial.length > 0
                  ? 'Tus suscripciones canceladas quedan abajo en el historial. Si querés volver, empezás una nueva con el kit que quieras.'
                  : 'Elegí un kit Limpack y empezá a recibir tus productos de limpieza cada mes. Sin permanencia.'}
              </p>
              <Link to="/cliente/nueva-suscripcion" className="mp-empty-cta">Empezar ahora →</Link>
            </div>
            {suscHistorial.length > 0 && (
              <BloqueHistorial
                historial={suscHistorial}
                expandido={mostrarHistorial}
                onToggle={() => setMostrarHistorial(v => !v)}
              />
            )}
          </>
        ) : (
          <>
            {/* Banner: falta pagar para activar la suscripción */}
            {susc?.estado === 'activa' && susc?.mp_preapproval_status !== 'authorized' && (
              <Link to={`/cliente/cargar-tarjeta/${susc.id}`} className="mp-pago-pendiente">
                <span className="mp-pago-pendiente-icon">⚠️</span>
                <div className="mp-pago-pendiente-body">
                  <strong>Tu suscripción está creada pero falta confirmar el pago.</strong>
                  <span>Hasta que no autorices el cobro con tu tarjeta, no entra a la ruta de entrega.</span>
                </div>
                <span className="mp-pago-pendiente-cta">Completar pago →</span>
              </Link>
            )}

            {/* Tabs de suscripciones activas — solo si hay más de 1 activa, o si hay historial */}
            {(suscActivas.length > 1 || suscHistorial.length > 0) && (
              <TabsSuscripciones
                suscripciones={suscActivas}
                suscActivaId={susc.id}
                onCambiar={cambiarSuscripcionActiva}
                historialCount={suscHistorial.length}
                onAbrirHistorial={() => setMostrarHistorial(v => !v)}
                historialAbierto={mostrarHistorial}
              />
            )}

            {/* HERO del kit (info pura, sin botones) */}
            <div className="mp-hero">
              <div className="mp-hero-pills">
                <span className="mp-hero-tag" style={{ color: estadoInfo.color }}>● {estadoInfo.label}</span>
                {susc.kit?.tier && TIER_BADGE[susc.kit.tier] && (
                  <span className="mp-hero-tier" style={{ background: TIER_BADGE[susc.kit.tier].bg, color: TIER_BADGE[susc.kit.tier].color }}>
                    {TIER_BADGE[susc.kit.tier].label}
                  </span>
                )}
              </div>
              <h2 className="mp-hero-h">{susc.kit?.nombre || 'Tu Kit'}</h2>
              <div className="mp-hero-sub">
                {susc.alias && <strong>{susc.alias} · </strong>}
                {susc.barrio || ''}
                {susc.direccion ? ` · ${susc.direccion}` : ''}
              </div>
              <div className="mp-hero-row">
                <div className="mp-hero-cell">
                  <div className="mp-hero-cell-label">Cuota mensual</div>
                  <div className="mp-hero-cell-val">{fmtMonto(susc.kit?.precio_susc || proximoPago?.monto)}</div>
                </div>
                <div className="mp-hero-cell">
                  <div className="mp-hero-cell-label">Próximo cobro</div>
                  <div className="mp-hero-cell-val">
                    {proximaFecha
                      ? `${fmtFechaCorta(diaAntesDe(proximaFecha))} · 16hs`
                      : '—'}
                    <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#64748B', marginTop: 2 }}>1 día hábil antes de tu entrega</span>
                  </div>
                </div>
                <div className="mp-hero-cell">
                  <div className="mp-hero-cell-label">Productos</div>
                  <div className="mp-hero-cell-val">{kitProductos.length}</div>
                </div>
              </div>
            </div>

            {/* Bloque "Próxima entrega" con info + acciones de la entrega */}
            <BloqueProximaEntrega
              proximaFecha={proximaFecha}
              ventana={susc.ventana_horaria}
              barrio={susc.barrio}
              direccion={susc.direccion}
              diaEntrega={susc.dia_entrega}
              onEditarEntrega={() => setModalEntrega(true)}
              onIrDireccion={() => navigate(`/cliente/cambiar-direccion/${susc.id}`)}
              onSumarProductos={() => navigate('/catalogo')}
              actualizadoAviso={actualizadoAviso}
            />

            {/* Acciones de la suscripción (afectan el plan entero) */}
            <BloqueAccionesSuscripcion
              estado={estado}
              onCambiarKit={() => navigate('/kits')}
              onPausar={handlePausar}
              onReactivar={handleReactivar}
              onCancelar={abrirModalCancelar}
            />

            {/* Próximas entregas — subido arriba del kit por feedback de Nico */}
            <div className="mp-cal mp-cal-standalone">
              <h3 className="mp-cal-title">Próximas entregas</h3>
              {proximaFecha && (
                <div className="mp-cal-item">
                  <div className="mp-cal-date">
                    <div className="mp-cal-day">{new Date(proximaFecha + 'T12:00:00').getDate()}</div>
                    <div className="mp-cal-mes">{['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][new Date(proximaFecha + 'T12:00:00').getMonth()]}</div>
                  </div>
                  <div className="mp-cal-info">
                    <div className="mp-cal-h">Próxima · {susc.kit_nombre}</div>
                    <div className="mp-cal-sub">{fmtFechaLarga(proximaFecha)}</div>
                  </div>
                  <span className="mp-cal-badge mp-badge-confirmed">Confirmada</span>
                </div>
              )}
              {proximoPago && proximaFecha && (() => {
                const fechaCobro = diaAntesDe(proximaFecha)
                return (
                  <div className="mp-cal-item">
                    <div className="mp-cal-date future">
                      <div className="mp-cal-day">{new Date(fechaCobro + 'T12:00:00').getDate()}</div>
                      <div className="mp-cal-mes">{['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][new Date(fechaCobro + 'T12:00:00').getMonth()]}</div>
                    </div>
                    <div className="mp-cal-info">
                      <div className="mp-cal-h">Próximo cobro · 16hs</div>
                      <div className="mp-cal-sub">{fmtMonto(proximoPago.monto)} · 1 día hábil antes de tu entrega</div>
                    </div>
                    <span className="mp-cal-badge mp-badge-warn">Pendiente</span>
                  </div>
                )
              })()}
            </div>

            {/* Lista productos */}
            <div className="mp-section-title">
              <span>🧴 Qué viene en tu kit</span>
            </div>
            <div className="mp-grid mp-grid-single">
              <div className="mp-kit-list">
                {kitProductos.length === 0 ? (
                  <div className="mp-kit-empty">No hay productos cargados en este kit todavía.</div>
                ) : (
                  <>
                    {kitProductos.map(p => (
                      p.es_regalo ? (
                        <div className="mp-kit-row is-regalo" key={p.sku} style={{
                          background: 'linear-gradient(90deg, #fffbeb, #fef3c7)',
                          border: '1px dashed #5BBDD8',
                        }}>
                          <div className="mp-kit-img mp-kit-img-regalo">
                            <span className="mp-kit-placeholder mp-kit-placeholder-regalo">🎁</span>
                          </div>
                          <div className="mp-kit-body">
                            <div className="mp-kit-name" style={{ color: '#78350f' }}>
                              Regalo sorpresa del mes
                              <span style={{
                                marginLeft: 8, fontSize: 10, fontWeight: 800, padding: '2px 8px',
                                background: 'linear-gradient(135deg, #FBBF24, #F59E0B)', color: '#fff',
                                borderRadius: 999, letterSpacing: '.06em', textTransform: 'uppercase',
                                verticalAlign: 'middle',
                              }}>🎁 Sorpresa</span>
                            </div>
                            <div className="mp-kit-meta" style={{ color: '#92400e' }}>Cambia cada mes · lo descubrís cuando llega 💌</div>
                          </div>
                          <div className="mp-kit-qty" style={{ color: '#78350f' }}>×{p.cantidad}</div>
                        </div>
                      ) : (
                        <div className="mp-kit-row" key={p.sku}>
                          <div className="mp-kit-img">
                            {p.imagen_url ? (
                              <img src={p.imagen_url} alt={p.nombre} />
                            ) : (
                              <span className="mp-kit-placeholder">{emojiPorNombre(p.nombre)}</span>
                            )}
                          </div>
                          <div className="mp-kit-body">
                            <div className="mp-kit-name">{p.nombre}</div>
                            <div className="mp-kit-meta">{p.presentacion}{p.marca ? ` · ${p.marca}` : ''}</div>
                          </div>
                          <div className="mp-kit-qty">×{p.cantidad}</div>
                        </div>
                      )
                    ))}
                    <div className="mp-kit-disclaimer">
                      <span className="mp-kit-disclaimer-icon">ℹ️</span>
                      <div className="mp-kit-disclaimer-text">
                        <b>Las marcas pueden cambiar según stock o promociones del mes.</b> Siempre te garantizamos la misma <b>cantidad, presentación y calidad</b>. Si tenés una marca preferida, avisanos por WhatsApp y la priorizamos.
                      </div>
                    </div>
                  </>
                )}
              </div>

            </div>

            {/* CTA: agregar otra suscripción — solo si todavía no tiene varias activas */}
            {suscActivas.length === 1 && (
              <Link to="/cliente/nueva-suscripcion" className="mp-add-susc">
                <div className="mp-add-susc-icon">＋</div>
                <div className="mp-add-susc-body">
                  <div className="mp-add-susc-h">Agregar otra suscripción</div>
                  <div className="mp-add-susc-sub">¿Querés sumar otro hogar? Una segunda dirección o regalársela a tu mamá, hijo, oficina… vos pagás, ellos reciben.</div>
                </div>
                <div className="mp-add-susc-arrow">›</div>
              </Link>
            )}

            {/* Historial de canceladas (colapsable) */}
            {suscHistorial.length > 0 && (
              <BloqueHistorial
                historial={suscHistorial}
                expandido={mostrarHistorial}
                onToggle={() => setMostrarHistorial(v => !v)}
              />
            )}
          </>
        )}

        {/* Modal cancelar suscripción */}
        {modalCancelar && (
          <ModalCancelarSuscripcion
            estado={modalCancelar}
            susc={susc}
            resumen={resumen}
            onConfirmar={ejecutarCancelacion}
            onCerrar={() => setModalCancelar(null)}
            onTerminado={() => { setModalCancelar(null); navigate('/cliente') }}
          />
        )}

        {/* Modal: editar día y horario de entrega */}
        {modalEntrega && susc && (
          <ModalCambiarDiaHorario
            susc={susc}
            estadoEntrega={calcEstadoEntrega(proximaFecha)}
            onCerrar={() => setModalEntrega(false)}
            onGuardado={() => { setModalEntrega(false); cargar() }}
          />
        )}

        {/* Modal genérico para pausar / reactivar / errores (reemplaza confirm/alert nativos) */}
        {modalAccion && (
          <ConfirmModal
            abierto={true}
            titulo={modalAccion.titulo}
            mensaje={modalAccion.mensaje}
            confirmLabel={modalAccion.confirmLabel}
            cancelLabel={modalAccion.cancelLabel === null ? null : (modalAccion.cancelLabel || 'Cancelar')}
            variant={modalAccion.variant || 'default'}
            procesando={modalAccion.procesando}
            onConfirm={modalAccion.onConfirm}
            onCancel={() => setModalAccion(null)}
          />
        )}
      </div>

      <style>{`
        .mp-wrap { max-width: 1320px; margin: 0 auto; padding: 16px 16px 32px }
        .mp-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .mp-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 4px; line-height: 1.5 }

        .mp-loading { padding: 40px; text-align: center; color: var(--cl-muted) }
        .mp-empty { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 40px 24px; text-align: center; margin-top: 24px }
        .mp-empty-icon { font-size: 48px; margin-bottom: 12px }
        .mp-empty h2 { font-size: 20px; font-weight: 800; color: var(--cl-text); letter-spacing: -.01em }
        .mp-empty p { font-size: 14px; color: var(--cl-muted); margin: 8px 0 18px; line-height: 1.5 }
        .mp-empty-cta { display: inline-block; background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); color: #fff; padding: 12px 22px; border-radius: 999px; font-weight: 800; font-size: 14px; text-decoration: none }

        .mp-hero-grid { display: grid; grid-template-columns: 1fr; gap: 10px; margin-top: 18px }
        .mp-hero { background: var(--cl-card); border-radius: 18px; padding: 22px; border: 1px solid var(--cl-line); margin-bottom: 0 }
        .mp-hero-pills { display: flex; align-items: center; gap: 8px; flex-wrap: wrap }
        .mp-hero-tag { font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase }
        .mp-hero-tier { font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px }
        .mp-hero-h { font-size: 24px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em; margin-top: 6px; line-height: 1.2 }
        .mp-hero-sub { font-size: 13px; color: var(--cl-muted); margin-top: 4px }
        .mp-hero-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top: 18px; padding-top: 18px; border-top: 1px solid var(--cl-line2) }
        .mp-hero-cell-label { font-size: 10px; color: var(--cl-muted2); font-weight: 800; letter-spacing: .08em; text-transform: uppercase }
        .mp-hero-cell-val { font-size: 14px; font-weight: 800; color: var(--cl-text); margin-top: 4px }

        .mp-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px }
        .mp-action {
          background: var(--cl-card); border: 1.5px solid var(--cl-line);
          padding: 14px; border-radius: 12px;
          display: flex; align-items: center; gap: 10px;
          cursor: pointer; font-family: inherit;
          text-decoration: none; color: var(--cl-text);
          transition: border-color .15s, transform .15s;
        }
        .mp-action:hover { border-color: var(--cl-teal); transform: translateY(-1px) }
        .mp-action.danger { border-color: #fecaca; color: #b91c1c }
        .mp-action.danger:hover { border-color: #b91c1c }
        .mp-action-icon { font-size: 20px }
        .mp-action-label { font-size: 13px; font-weight: 800 }

        .mp-section-title { font-size: 16px; font-weight: 900; color: var(--cl-text); margin: 26px 0 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap }
        .mp-live-pill {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase;
          background: #dcfce7; color: #166534;
          padding: 5px 11px; border-radius: 999px;
          animation: mp-live-fade .3s ease-out;
        }
        @keyframes mp-live-fade {
          from { opacity: 0; transform: translateY(-4px) }
          to { opacity: 1; transform: translateY(0) }
        }

        .mp-grid { display: grid; grid-template-columns: 1fr; gap: 14px }
        .mp-grid-single, .mp-grid.mp-grid-single { grid-template-columns: 1fr }
        .mp-cal-standalone { margin-bottom: 18px }

        .mp-kit-list { background: var(--cl-card); border-radius: 14px; border: 1px solid var(--cl-line); overflow: hidden }
        .mp-kit-empty { padding: 24px; text-align: center; color: var(--cl-muted); font-size: 13px }
        .mp-kit-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid var(--cl-line2) }
        .mp-kit-row:last-child { border-bottom: 0 }
        .mp-kit-img {
          width: 52px; height: 64px; border-radius: 10px; flex-shrink: 0; overflow: hidden;
          background: linear-gradient(135deg, #f8f6f1, #ece6da);
          border: 1px solid rgba(197, 160, 89, .25);
          display: flex; align-items: center; justify-content: center;
        }
        .mp-kit-img img { width: 100%; height: 100%; object-fit: contain; display: block; padding: 4px }
        .mp-kit-placeholder { font-size: 28px; opacity: .9; line-height: 1; filter: saturate(.9) }
        .mp-kit-img-regalo {
          background: linear-gradient(135deg, #fef9c3, #fde68a);
          border-color: #5BBDD8;
        }
        .mp-kit-placeholder-regalo { font-size: 30px; opacity: 1 }

        .mp-kit-disclaimer {
          display: flex; gap: 10px; align-items: flex-start;
          padding: 14px 16px;
          background: linear-gradient(90deg, #f0f9ff, #ecfeff);
          border-top: 1px dashed #bae6fd;
        }
        .mp-kit-disclaimer-icon { font-size: 18px; line-height: 1.3; flex-shrink: 0 }
        .mp-kit-disclaimer-text {
          font-size: 12px; color: #075985; line-height: 1.55;
        }
        .mp-kit-disclaimer-text b { color: #0c4a6e; font-weight: 800 }
        .mp-kit-body { flex: 1; min-width: 0 }
        .mp-kit-name { font-size: 13px; font-weight: 700; color: var(--cl-text); line-height: 1.3 }
        .mp-kit-meta { font-size: 11px; color: var(--cl-muted); margin-top: 2px }
        .mp-kit-qty { background: var(--cl-line2); border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 800; color: var(--cl-text); flex-shrink: 0 }

        .mp-cal { display: flex; flex-direction: column; gap: 10px }
        .mp-cal-title { font-size: 13px; font-weight: 800; color: var(--cl-text); margin-bottom: 4px; letter-spacing: .02em }
        .mp-cal-item { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; padding: 14px; display: flex; align-items: center; gap: 12px }
        .mp-cal-date { background: var(--cl-navy); color: #fff; width: 50px; height: 50px; border-radius: 10px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center }
        .mp-cal-date.future { background: var(--cl-muted2) }
        .mp-cal-day { font-size: 18px; font-weight: 900; line-height: 1 }
        .mp-cal-mes { font-size: 9px; opacity: .85; text-transform: uppercase; letter-spacing: .06em; margin-top: 2px }
        .mp-cal-info { flex: 1; min-width: 0 }
        .mp-cal-h { font-size: 13px; font-weight: 800; color: var(--cl-text) }
        .mp-cal-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }
        .mp-cal-badge { font-size: 9px; font-weight: 800; padding: 4px 9px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase; flex-shrink: 0 }
        .mp-badge-confirmed { background: #e6fafc; color: #0f7480 }
        .mp-badge-warn { background: #fef3c7; color: #92400e }

        .mp-otras-grid { display: grid; grid-template-columns: 1fr; gap: 10px; margin-bottom: 18px }
        .mp-otra {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 14px; padding: 16px;
        }
        .mp-otra-tag { font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase }
        .mp-otra-h { font-size: 17px; font-weight: 900; color: var(--cl-text); margin-top: 4px; letter-spacing: -.01em }
        .mp-otra-sub { font-size: 12px; color: var(--cl-muted); margin-top: 4px; line-height: 1.45 }
        .mp-otra-precio { font-size: 14px; font-weight: 800; color: var(--cl-text); margin-top: 8px }

        .mp-add-susc {
          margin-top: 18px;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          border: 1.5px dashed #86efac; border-radius: 16px;
          padding: 18px; display: flex; align-items: center; gap: 14px;
          text-decoration: none; color: var(--cl-text);
          transition: transform .15s, border-color .15s;
        }
        .mp-add-susc:hover { transform: translateY(-2px); border-color: #16a34a; border-style: solid }
        .mp-add-susc-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: #16a34a; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 26px; font-weight: 300; flex-shrink: 0;
        }
        .mp-add-susc-body { flex: 1; min-width: 0 }
        .mp-add-susc-h { font-size: 14px; font-weight: 800; color: #065f46 }
        .mp-add-susc-sub { font-size: 12.5px; color: #047857; margin-top: 4px; line-height: 1.45 }
        .mp-add-susc-arrow { color: #16a34a; font-size: 22px; font-weight: 800 }

        @media (min-width: 700px) {
          .mp-otras-grid { grid-template-columns: 1fr 1fr }
        }

        @media (min-width: 900px) {
          .mp-wrap { padding: 36px 44px }
          .mp-headline h1 { font-size: 32px }
          .mp-hero-grid { grid-template-columns: 1.4fr 1fr; gap: 20px; margin-top: 24px }
          .mp-hero { padding: 26px }
          .mp-hero-h { font-size: 28px }
          .mp-actions { grid-template-columns: 1fr 1fr; gap: 12px; align-content: start }
          .mp-action { padding: 16px }
          .mp-section-title { font-size: 18px; margin: 32px 0 14px }
          .mp-grid { grid-template-columns: 1.4fr 1fr; gap: 24px }
          .mp-grid.mp-grid-single { grid-template-columns: 1fr }
          .mp-kit-row { padding: 14px 18px; gap: 16px }
          .mp-kit-img { width: 64px; height: 80px }
          .mp-kit-placeholder { font-size: 34px }
          .mp-kit-placeholder-regalo { font-size: 36px }
          .mp-kit-name { font-size: 14px }
          .mp-kit-meta { font-size: 12px }
          .mp-kit-disclaimer { padding: 16px 20px }
          .mp-kit-disclaimer-text { font-size: 12.5px }
        }

        /* ─── Banner falta pagar ─── */
        .mp-pago-pendiente {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 18px;
          margin: 0 0 18px;
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border: 1.5px solid #f59e0b;
          border-radius: 14px;
          text-decoration: none; color: #78350f;
          transition: transform .12s, box-shadow .12s;
        }
        .mp-pago-pendiente:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(245,158,11,.18) }
        .mp-pago-pendiente-icon { font-size: 26px; flex-shrink: 0 }
        .mp-pago-pendiente-body { flex: 1; display: flex; flex-direction: column; gap: 2px; line-height: 1.4 }
        .mp-pago-pendiente-body strong { font-size: 14px; font-weight: 800; color: #92400e }
        .mp-pago-pendiente-body span { font-size: 12.5px; color: #b45309 }
        .mp-pago-pendiente-cta {
          padding: 9px 16px; background: #b45309; color: #fff;
          border-radius: 999px; font-size: 13px; font-weight: 800;
          white-space: nowrap; flex-shrink: 0;
        }
        @media (max-width: 540px) {
          .mp-pago-pendiente { flex-wrap: wrap }
          .mp-pago-pendiente-cta { width: 100%; text-align: center }
        }

        /* ─── Modal cancelar suscripción ─── */
        .mc-bg {
          position: fixed; inset: 0; z-index: 90;
          background: rgba(15,31,61,.58);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .mc-card {
          background: #fff; border-radius: 16px;
          max-width: 520px; width: 100%;
          padding: 24px;
          box-shadow: 0 30px 60px rgba(0,0,0,.25);
          max-height: 90vh; overflow-y: auto;
        }
        .mc-h { font-size: 20px; font-weight: 900; color: #1A3461; margin: 0 0 6px }
        .mc-sub { font-size: 13.5px; color: #64748b; margin: 0 0 18px; line-height: 1.5 }
        .mc-bloque {
          padding: 12px 14px; border-radius: 10px;
          margin-bottom: 10px; font-size: 13px; line-height: 1.5;
          display: flex; gap: 10px; align-items: flex-start;
        }
        .mc-bloque-ok { background: #ecfdf5; border: 1px solid #6ee7b7; color: #065f46 }
        .mc-bloque-warn { background: #fffbeb; border: 1px solid #fcd34d; color: #78350f }
        .mc-bloque-info { background: #eff6ff; border: 1px solid #93c5fd; color: #1e3a8a }
        .mc-bloque-icon { font-size: 18px; flex-shrink: 0; line-height: 1 }
        .mc-bloque strong { font-weight: 800 }
        .mc-label { font-size: 12px; font-weight: 800; color: #475569; letter-spacing: .04em; text-transform: uppercase; margin: 16px 0 6px }
        .mc-select {
          width: 100%; padding: 10px 12px; border: 1px solid #cbd5e1;
          border-radius: 8px; font-size: 14px; font-family: inherit;
          background: #fff; color: #1A3461;
        }
        .mc-actions { display: flex; gap: 10px; margin-top: 22px; flex-wrap: wrap }
        .mc-btn-volver {
          flex: 1; min-width: 120px;
          background: #fff; border: 1.5px solid #cbd5e1;
          padding: 12px 16px; border-radius: 10px;
          font-size: 14px; font-weight: 700; color: #475569;
          cursor: pointer; font-family: inherit;
        }
        .mc-btn-volver:hover { background: #f8fafc }
        .mc-btn-cancelar {
          flex: 1; min-width: 120px;
          background: #dc2626; color: #fff; border: 0;
          padding: 12px 16px; border-radius: 10px;
          font-size: 14px; font-weight: 800;
          cursor: pointer; font-family: inherit;
        }
        .mc-btn-cancelar:hover { background: #991b1b }
        .mc-btn-cancelar:disabled { opacity: .6; cursor: not-allowed }
        .mc-procesando { text-align: center; padding: 28px 12px }
        .mc-procesando-spinner {
          width: 40px; height: 40px; margin: 0 auto 12px;
          border: 3px solid #e2e8f0; border-top-color: #1a5cb8;
          border-radius: 50%; animation: mc-spin .9s linear infinite;
        }
        @keyframes mc-spin { to { transform: rotate(360deg) } }
        .mc-listo-icon {
          width: 64px; height: 64px; margin: 0 auto 14px;
          background: #10b981; color: #fff;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          font-size: 36px; font-weight: 900;
        }
        .mc-listo-h { font-size: 20px; font-weight: 900; color: #065f46; text-align: center; margin: 0 0 10px }
        .mc-resumen-item { display: flex; gap: 10px; padding: 8px 0; font-size: 13.5px; color: #334155 }
        .mc-resumen-item strong { color: #1A3461; font-weight: 700 }

        .mp-tabs { background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; padding: 10px 12px; margin: 0 0 14px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .mp-tabs-label { font-size: 10px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: #64748B; padding: 0 4px; flex-shrink: 0; }
        .mp-tabs-list { display: flex; gap: 6px; flex: 1; overflow-x: auto; padding-bottom: 2px; }
        .mp-tab { background: #F8FAFC; color: #1A3461; border: 1px solid #E2E8F0; padding: 8px 12px; border-radius: 10px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; flex-shrink: 0; transition: all 0.15s; }
        .mp-tab:hover { border-color: #1A3461; }
        .mp-tab.is-active { background: #1A3461; color: #fff; border-color: #1A3461; font-weight: 700; }
        .mp-tab-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .mp-tab-alias { font-weight: 700; }
        .mp-tab.is-active .mp-tab-alias { font-weight: 800; }
        .mp-tab-meta { font-size: 11px; opacity: 0.7; }
        .mp-tab-add { background: transparent; color: #00A8B5; border: 1.5px dashed #00A8B5; text-decoration: none; font-weight: 700; }
        .mp-tab-add:hover { background: #00A8B5; color: #fff; border-style: solid; }
        .mp-tab-historial { background: #F1F5F9; color: #64748B; border: 1px solid #CBD5E1; font-weight: 600; }
        .mp-tab-historial:hover { background: #E2E8F0; color: #1A3461; }
        .mp-tab-historial.is-active { background: #1A3461; color: #fff; border-color: #1A3461; }

        .mp-historial { background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; margin: 14px 0 0; overflow: hidden; }
        .mp-historial.is-open { border-color: #CBD5E1; }
        .mp-historial-toggle { width: 100%; display: flex; align-items: center; gap: 10px; background: transparent; border: 0; padding: 14px 16px; cursor: pointer; font-family: inherit; text-align: left; transition: background 0.15s; }
        .mp-historial-toggle:hover { background: #F8FAFC; }
        .mp-historial-toggle-icon { font-size: 18px; }
        .mp-historial-toggle-label { flex: 1; color: #475569; font-size: 14px; }
        .mp-historial-toggle-label strong { color: #1A3461; font-weight: 800; }
        .mp-historial-toggle-chev { color: #64748B; font-size: 14px; }
        .mp-historial-lista { border-top: 1px solid #E2E8F0; padding: 6px 0; }
        .mp-historial-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #F1F5F9; }
        .mp-historial-row:last-child { border-bottom: 0; }
        .mp-historial-row-info { flex: 1; min-width: 0; }
        .mp-historial-row-title { color: #1A3461; font-size: 14px; line-height: 1.3; }
        .mp-historial-row-title strong { font-weight: 700; }
        .mp-historial-row-sub { color: #64748B; font-weight: 500; }
        .mp-historial-row-meta { color: #94A3B8; font-size: 12px; margin-top: 2px; }
        .mp-historial-row-cta { background: transparent; color: #00A8B5; border: 1px solid #00A8B5; padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 700; text-decoration: none; white-space: nowrap; flex-shrink: 0; transition: all 0.15s; }
        .mp-historial-row-cta:hover { background: #00A8B5; color: #fff; }
        @media (max-width: 640px) {
          .mp-historial-row { flex-direction: column; align-items: stretch; gap: 8px; }
          .mp-historial-row-cta { text-align: center; }
        }
        @media (max-width: 640px) {
          .mp-tabs { padding: 10px; gap: 8px; }
          .mp-tabs-label { display: none; }
          .mp-tab { padding: 8px 10px; font-size: 12px; }
          .mp-tab-meta { display: none; }
        }

        .mp-pe { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 18px; margin: 0 0 10px; backdrop-filter: blur(8px); }
        .mp-pe-d1-editable { border: 1.5px solid rgba(245, 158, 11, 0.55); }
        .mp-pe-d1-cerrado  { border: 1.5px solid rgba(0, 168, 181, 0.55); }
        .mp-pe-banner { padding: 10px 14px; border-radius: 10px; font-size: 13px; line-height: 1.45; margin-bottom: 14px; display: flex; align-items: flex-start; gap: 8px; }
        .mp-pe-banner-warn { background: rgba(245, 158, 11, 0.15); color: #FBBF24; border: 1px solid rgba(245, 158, 11, 0.3); }
        .mp-pe-banner-info { background: rgba(91, 189, 216, 0.12); color: #5BBDD8; border: 1px solid rgba(91, 189, 216, 0.3); }
        .mp-pe-body { display: flex; align-items: flex-start; gap: 14px; flex-wrap: wrap; }
        .mp-pe-cal { display: flex; align-items: center; justify-content: center; flex-direction: column; width: 56px; border-radius: 10px; padding: 8px 4px; flex-shrink: 0; color: #fff; }
        .mp-pe-cal-a-tiempo    { background: linear-gradient(180deg, #00A8B5, #008a95); }
        .mp-pe-cal-d1-editable { background: linear-gradient(180deg, #F59E0B, #D97706); }
        .mp-pe-cal-d1-cerrado  { background: linear-gradient(180deg, #1A3461, #0F2547); }
        .mp-pe-cal-dia { font-size: 9.5px; font-weight: 800; letter-spacing: 0.08em; opacity: 0.92; }
        .mp-pe-cal-num { font-size: 22px; font-weight: 900; line-height: 1; margin: 2px 0; }
        .mp-pe-cal-mes { font-size: 9px; opacity: 0.92; }
        .mp-pe-info { flex: 1; min-width: 200px; }
        .mp-pe-eyebrow { font-size: 10px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; }
        .mp-pe-fecha { font-size: 15px; font-weight: 800; color: var(--cl-text); margin: 2px 0 4px; letter-spacing: -0.01em; line-height: 1.25; }
        .mp-pe-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 12.5px; color: var(--cl-muted); }
        .mp-pe-sep { color: rgba(255,255,255,0.2); }
        .mp-pe-hint { font-size: 12px; color: var(--cl-muted); margin-top: 6px; line-height: 1.45; }
        .mp-pe-acciones-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-top: 12px; }
        .mp-pe-accion { background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(0, 168, 181, 0.35); padding: 9px 10px; border-radius: 9px; cursor: pointer; font-family: inherit; display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 7px; transition: all 0.15s; min-height: 36px; }
        .mp-pe-accion:hover { background: #00A8B5; border-color: #00A8B5; transform: translateY(-1px); }
        .mp-pe-accion-icon { font-size: 14px; line-height: 1; }
        .mp-pe-accion-label { font-size: 12px; font-weight: 700; color: var(--cl-text); text-align: left; line-height: 1.15; letter-spacing: 0.01em; }
        .mp-pe-accion-dim { background: rgba(255, 255, 255, 0.03); border-color: rgba(255, 255, 255, 0.1); }
        .mp-pe-accion-dim .mp-pe-accion-label { color: var(--cl-muted); }
        .mp-pe-accion-dim:hover { background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.15); transform: none; }
        @media (max-width: 560px) {
          .mp-pe-acciones-grid { grid-template-columns: 1fr; gap: 5px; }
          .mp-pe-accion { justify-content: flex-start; padding: 9px 12px; }
        }

        .mp-pe-btn-wa { background: #fff; border: 1.5px solid #25D366; color: #25D366; padding: 10px 14px; border-radius: 10px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; transition: all 0.15s; }
        .mp-pe-btn-wa:hover { background: #25D366; color: #fff; }
        .mp-pe-urgent { display: flex; justify-content: center; margin: 14px 0 0; width: 100%; }

        .mp-asusc { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 12px 16px; margin: 0 0 10px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; backdrop-filter: blur(8px); }
        .mp-asusc-titulo { font-size: 10px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--cl-muted); flex-shrink: 0; }
        .mp-asusc-grid { display: flex; gap: 8px; flex: 1; justify-content: flex-end; flex-wrap: wrap; }
        .mp-asusc-btn { background: rgba(255,255,255,0.06); border: 1px solid var(--cl-line); padding: 9px 16px; border-radius: 10px; font-weight: 600; font-size: 13px; color: var(--cl-text); cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; transition: all 0.15s; }
        .mp-asusc-btn:hover { border-color: #00A8B5; background: rgba(0,168,181,0.12); }
        .mp-asusc-btn-danger { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.35); color: #FCA5A5; }
        .mp-asusc-btn-danger:hover { background: rgba(239,68,68,0.18); border-color: #EF4444; color: #FCA5A5; }
        @media (max-width: 640px) {
          .mp-asusc { flex-direction: column; align-items: stretch; gap: 10px; padding: 14px 16px; }
          .mp-asusc-grid { display: grid; grid-template-columns: repeat(3, 1fr); width: 100%; }
          .mp-asusc-btn { padding: 11px 6px; justify-content: center; font-size: 12px; }
        }

        .mp-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
        .mp-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
        .mp-modal-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 22px 12px; border-bottom: 1px solid #F1F5F9; }
        .mp-modal-head h2 { font-size: 20px; font-weight: 800; margin: 0; color: #1A3461; letter-spacing: -0.01em; }
        .mp-modal-head p { font-size: 13px; color: #64748B; margin: 4px 0 0; }
        .mp-modal-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #64748B; padding: 4px 10px; }
        .mp-modal-close:hover { color: #1A3461; }
        .mp-modal-body { padding: 18px 22px 4px; }
        .mp-modal-foot { display: flex; gap: 10px; padding: 14px 22px 20px; border-top: 1px solid #F1F5F9; }
        .mp-modal-btn-primary { flex: 1; background: #00A8B5; color: #fff; border: none; padding: 12px; border-radius: 10px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; transition: background 0.15s; }
        .mp-modal-btn-primary:hover:not(:disabled) { background: #008a95; }
        .mp-modal-btn-primary:disabled { opacity: 0.6; cursor: wait; }
        .mp-modal-btn-secondary { background: #fff; color: #1A3461; border: 1.5px solid #E2E8F0; padding: 12px 16px; border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; transition: border-color 0.15s; }
        .mp-modal-btn-secondary:hover { border-color: #1A3461; }
        .mp-modal-btn-secondary:disabled { opacity: 0.6; cursor: wait; }
        .mp-field { display: flex; flex-direction: column; margin-bottom: 14px; }
        .mp-field > span { font-size: 12px; font-weight: 700; color: #1A3461; margin-bottom: 6px; }
        .mp-field-hint { font-size: 11px; font-weight: 500; color: #64748B; font-style: normal; }
        .mp-field select { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 11px 12px; font-size: 14px; color: #1A3461; font-family: inherit; }
        .mp-field select:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,0.18); }
        .mp-vent-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
        .mp-vent-opt { display: flex; align-items: center; gap: 12px; background: #F8FAFC; border: 1.5px solid #E2E8F0; border-radius: 10px; padding: 12px 14px; cursor: pointer; font-family: inherit; transition: all 0.15s; text-align: left; }
        .mp-vent-opt:hover { border-color: #00A8B5; background: #fff; }
        .mp-vent-opt.is-active { border-color: #00A8B5; background: #ECFEFF; }
        .mp-vent-emoji { font-size: 22px; }
        .mp-vent-label { font-size: 14px; font-weight: 700; color: #1A3461; }
        .mp-modal-error { background: #FEE2E2; color: #991B1B; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin: 8px 0 0; }

        @media (max-width: 640px) {
          .mp-pe-body { gap: 12px; }
          .mp-pe-cal { width: 64px; padding: 10px 4px; }
          .mp-pe-cal-num { font-size: 26px; }
          .mp-pe-fecha { font-size: 16px; }
          .mp-pe-accion-label { font-size: 12px; }
          .mp-asusc-btn { font-size: 12px; padding: 11px 6px; }
        }
      `}</style>
    </ClienteLayout>
  )
}

/* ─── Modal cancelar suscripción ─── */
function ModalCancelarSuscripcion({ estado, susc, resumen, onConfirmar, onCerrar, onTerminado }) {
  const [motivo, setMotivo] = useState('')

  // ESC para cerrar (solo en estado confirmar)
  useEffect(() => {
    if (estado !== 'confirmar') return
    const onKey = (e) => { if (e.key === 'Escape') onCerrar() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [estado, onCerrar])

  const tienePreapproval = !!susc?.mp_preapproval_id
  const proximaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  const tienePagoPendiente = !!resumen?.proximoPago

  if (estado === 'procesando') {
    return (
      <div className="mc-bg">
        <div className="mc-card">
          <div className="mc-procesando">
            <div className="mc-procesando-spinner" />
            <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Cancelando tu suscripción…</p>
          </div>
        </div>
      </div>
    )
  }

  if (typeof estado === 'object' && estado?.tipo === 'resultado') {
    const r = estado.resumen || {}
    return (
      <div className="mc-bg" onClick={onTerminado}>
        <div className="mc-card" onClick={e => e.stopPropagation()}>
          <div className="mc-listo-icon">✓</div>
          <h2 className="mc-listo-h">Suscripción cancelada</h2>
          <p className="mc-sub" style={{ textAlign: 'center' }}>
            Listo. Ya no se cobra ni se entrega más.
          </p>
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', margin: '14px 0' }}>
            {r.pagos_cancelados > 0 && (
              <div className="mc-resumen-item">
                <span>💰</span>
                <span>Anulamos <strong>{r.pagos_cancelados} pago{r.pagos_cancelados === 1 ? '' : 's'} pendiente{r.pagos_cancelados === 1 ? '' : 's'}</strong>.</span>
              </div>
            )}
            {r.entregas_canceladas > 0 && (
              <div className="mc-resumen-item">
                <span>📦</span>
                <span>Cancelamos <strong>{r.entregas_canceladas} entrega{r.entregas_canceladas === 1 ? '' : 's'}</strong> que todavía no estaba{r.entregas_canceladas === 1 ? '' : 'n'} armada{r.entregas_canceladas === 1 ? '' : 's'}.</span>
              </div>
            )}
            {r.entregas_en_corte > 0 && (
              <div className="mc-resumen-item">
                <span>🚚</span>
                <span><strong>{r.entregas_en_corte} entrega{r.entregas_en_corte === 1 ? '' : 's'}</strong> ya armada{r.entregas_en_corte === 1 ? '' : 's'} sigue{r.entregas_en_corte === 1 ? '' : 'n'} su curso — la{r.entregas_en_corte === 1 ? '' : 's'} recibís igual.</span>
              </div>
            )}
            {r.requiere_cancelar_mp && (
              <div className="mc-resumen-item">
                <span>💳</span>
                <span>Cancelamos el <strong>cobro automático de tu tarjeta</strong>.</span>
              </div>
            )}
            {!r.pagos_cancelados && !r.entregas_canceladas && !r.entregas_en_corte && !r.requiere_cancelar_mp && (
              <div className="mc-resumen-item">
                <span>✨</span>
                <span>No había nada pendiente. Suscripción cancelada limpia.</span>
              </div>
            )}
          </div>
          <button className="mc-btn-cancelar" style={{ background: '#1a5cb8', width: '100%' }} onClick={onTerminado}>
            Volver a mi cuenta →
          </button>
        </div>
      </div>
    )
  }

  // estado === 'confirmar'
  return (
    <div className="mc-bg" onClick={onCerrar}>
      <div className="mc-card" onClick={e => e.stopPropagation()}>
        <h2 className="mc-h">¿Cancelar tu suscripción?</h2>
        <p className="mc-sub">
          Antes de seguir, mirá qué pasa con lo que tenés en marcha.
        </p>

        {/* Bloque: entrega ya armada (corte D-1 16h pasado) */}
        {proximaFecha && (
          <div className="mc-bloque mc-bloque-info">
            <span className="mc-bloque-icon">🚚</span>
            <div>
              Si tu <strong>próxima entrega ya pasó el corte (D-1 16:00 hs)</strong>, se te entrega igual. Si todavía no llegó al corte, la cancelamos automáticamente.
            </div>
          </div>
        )}

        {/* Bloque: pago pendiente */}
        {tienePagoPendiente && (
          <div className="mc-bloque mc-bloque-warn">
            <span className="mc-bloque-icon">💰</span>
            <div>
              Tenés un <strong>pago pendiente de ${Math.ceil(Number(resumen.proximoPago.monto)).toLocaleString('es-UY')}</strong>. Si todavía no se cobró, lo anulamos. Si ya está cobrado, no se reembolsa automáticamente — escribinos por WhatsApp si fue un error.
            </div>
          </div>
        )}

        {/* Bloque: cobro automático */}
        {tienePreapproval && (
          <div className="mc-bloque mc-bloque-info">
            <span className="mc-bloque-icon">💳</span>
            <div>
              Cancelamos el <strong>cobro automático de tu tarjeta</strong>. No te van a debitar más.
            </div>
          </div>
        )}

        {/* Bloque: bonificación 3 meses */}
        {resumen?.bonif?.aplica && resumen.bonif.mesesRestantes > 0 && (
          <div className="mc-bloque mc-bloque-warn">
            <span className="mc-bloque-icon">🎁</span>
            <div>
              Estás en la <strong>bonificación 3 meses</strong> (mes {resumen.bonif.mesActual} de 3). Si cancelás ahora, perdés los próximos {resumen.bonif.mesesRestantes} mes{resumen.bonif.mesesRestantes === 1 ? '' : 'es'} con descuento. El extra del mes 1 ($300 aprox) no se devuelve.
            </div>
          </div>
        )}

        {/* Bloque tranquilizador */}
        <div className="mc-bloque mc-bloque-ok">
          <span className="mc-bloque-icon">✨</span>
          <div>
            <strong>Podés volver cuando quieras.</strong> No hay permanencia. Si te suscribís de nuevo, arrancás desde cero (mes 1 a precio compra única).
          </div>
        </div>

        <div className="mc-label">¿Por qué cancelás? (opcional)</div>
        <select className="mc-select" value={motivo} onChange={e => setMotivo(e.target.value)}>
          <option value="">— Elegí un motivo —</option>
          <option value="muy_caro">Me resulta caro</option>
          <option value="no_uso_todo">No uso todos los productos</option>
          <option value="prefiero_supermercado">Prefiero el supermercado</option>
          <option value="mudanza">Me mudo o cambio de barrio</option>
          <option value="no_satisfecho">No me convencen las marcas</option>
          <option value="problema_entrega">Hubo problemas con la entrega</option>
          <option value="prueba">Era solo para probar</option>
          <option value="otro">Otro motivo</option>
        </select>

        <div className="mc-actions">
          <button type="button" className="mc-btn-volver" onClick={onCerrar}>
            Mejor, no
          </button>
          <button type="button" className="mc-btn-cancelar" onClick={() => onConfirmar(motivo)}>
            Sí, cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

function TabsSuscripciones({ suscripciones, suscActivaId, onCambiar, historialCount = 0, onAbrirHistorial, historialAbierto = false }) {
  function aliasCorto(s) {
    if (s.alias) return s.alias
    if (s.barrio) return s.barrio
    return s.kit?.nombre || 'Suscripción'
  }
  return (
    <div className="mp-tabs">
      <div className="mp-tabs-label">TUS SUSCRIPCIONES</div>
      <div className="mp-tabs-list">
        {suscripciones.map(s => {
          const activa = s.id === suscActivaId
          const dotColor = s.estado === 'activa' ? '#16a34a' : s.estado === 'pausada' ? '#f59e0b' : '#94a3b8'
          return (
            <button
              key={s.id}
              type="button"
              className={`mp-tab ${activa ? 'is-active' : ''}`}
              onClick={() => onCambiar(s.id)}
              title={`${aliasCorto(s)} · ${s.kit?.nombre || ''}`}
            >
              <span className="mp-tab-dot" style={{ background: dotColor }} />
              <span className="mp-tab-alias">{aliasCorto(s)}</span>
              <span className="mp-tab-meta">{s.kit?.nombre ? `· ${s.kit.nombre}` : ''}</span>
            </button>
          )
        })}
        <Link to="/cliente/nueva-suscripcion" className="mp-tab mp-tab-add" title="Agregar otra suscripción">
          ＋ Agregar
        </Link>
        {historialCount > 0 && (
          <button
            type="button"
            className={`mp-tab mp-tab-historial ${historialAbierto ? 'is-active' : ''}`}
            onClick={onAbrirHistorial}
            title="Ver suscripciones canceladas"
          >
            🗂️ Historial ({historialCount})
          </button>
        )}
      </div>
    </div>
  )
}

function BloqueHistorial({ historial, expandido, onToggle }) {
  return (
    <div className={`mp-historial ${expandido ? 'is-open' : ''}`}>
      <button type="button" className="mp-historial-toggle" onClick={onToggle}>
        <span className="mp-historial-toggle-icon">🗂️</span>
        <span className="mp-historial-toggle-label">
          Historial de suscripciones <strong>({historial.length})</strong>
        </span>
        <span className="mp-historial-toggle-chev">{expandido ? '▴' : '▾'}</span>
      </button>
      {expandido && (
        <div className="mp-historial-lista">
          {historial.map(s => {
            const fechaCancel = s.cancelled_at || s.updated_at
            const fechaTxt = fechaCancel
              ? new Date(fechaCancel).toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' })
              : '—'
            return (
              <div className="mp-historial-row" key={s.id}>
                <div className="mp-historial-row-info">
                  <div className="mp-historial-row-title">
                    <strong>{s.kit?.nombre || s.kit_nombre || 'Kit'}</strong>
                    {(s.alias || s.barrio) && <span className="mp-historial-row-sub"> · {s.alias || s.barrio}</span>}
                  </div>
                  <div className="mp-historial-row-meta">
                    Cancelada {fechaTxt}
                    {s.direccion ? ` · ${s.direccion}` : ''}
                  </div>
                </div>
                <Link
                  to="/cliente/nueva-suscripcion"
                  className="mp-historial-row-cta"
                  title="Empezar una nueva suscripción"
                >
                  Volver a suscribirme →
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function BloqueProximaEntrega({ proximaFecha, ventana, barrio, direccion, diaEntrega, onEditarEntrega, onIrDireccion, onSumarProductos, actualizadoAviso }) {
  const estado = calcEstadoEntrega(proximaFecha)
  if (!proximaFecha || estado.tipo === 'pasada' || estado.tipo === 'sin_entrega') return null

  const d = new Date(proximaFecha + 'T12:00:00')
  const diaCorto = ['DOM','LUN','MAR','MIÉ','JUE','VIE','SÁB'][d.getDay()]
  const numero = d.getDate()
  const mesCorto = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'][d.getMonth()]
  const ventanaInfo = VENTANAS_HORARIAS.find(v => v.key === ventana)
  const fechaLarga = fmtFechaLarga(proximaFecha)

  const variantes = {
    a_tiempo:    { clase: 'a-tiempo',    eyebrow: 'PRÓXIMA ENTREGA', eyebrowColor: '#00A8B5' },
    d1_editable: { clase: 'd1-editable', eyebrow: 'MAÑANA TE LLEGA', eyebrowColor: '#D97706' },
    d1_cerrado:  { clase: 'd1-cerrado',  eyebrow: estado.dias === 0 ? 'HOY TE LLEGA' : 'MAÑANA TE LLEGA', eyebrowColor: '#1A3461' },
  }
  const v = variantes[estado.tipo] || variantes.a_tiempo

  const editableAhora = estado.tipo === 'a_tiempo' || estado.tipo === 'd1_editable'

  return (
    <div className={`mp-pe mp-pe-${v.clase}`}>
      {estado.tipo === 'd1_editable' && (
        <div className="mp-pe-banner mp-pe-banner-warn">
          <span aria-hidden="true">⚠️</span>
          <span><strong>Tu entrega es mañana.</strong> Tenés hasta las <strong>{estado.deadline}</strong> para cambiar día u horario. Después armamos el kit.</span>
        </div>
      )}
      {estado.tipo === 'd1_cerrado' && (
        <div className="mp-pe-banner mp-pe-banner-info">
          <span aria-hidden="true">📦</span>
          <span><strong>{estado.msg}</strong> Los cambios desde acá afectan la <strong>próxima entrega</strong>.</span>
        </div>
      )}

      <div className="mp-pe-body">
        <div className={`mp-pe-cal mp-pe-cal-${v.clase}`}>
          <div className="mp-pe-cal-dia">{diaCorto}</div>
          <div className="mp-pe-cal-num">{numero}</div>
          <div className="mp-pe-cal-mes">{mesCorto}</div>
        </div>

        <div className="mp-pe-info">
          <div className="mp-pe-eyebrow" style={{ color: v.eyebrowColor }}>
            {v.eyebrow}
            {actualizadoAviso && <span className="mp-live-pill" style={{ marginLeft: 8 }}>● en vivo</span>}
          </div>
          <div className="mp-pe-fecha">{fechaLarga}</div>
          <div className="mp-pe-meta">
            <span><span aria-hidden="true">🕐</span> {ventanaInfo ? `${ventanaInfo.corto.replace(/^./, c => c.toUpperCase())} ${ventanaInfo.hi.slice(0,-3)}–${ventanaInfo.hf.slice(0,-3)}hs` : 'Horario por confirmar'}</span>
            {(barrio || direccion) && (
              <>
                <span className="mp-pe-sep">·</span>
                <span><span aria-hidden="true">📍</span> {barrio || direccion}</span>
              </>
            )}
          </div>
          {estado.tipo === 'a_tiempo' && estado.dias >= 2 && (
            <div className="mp-pe-hint">Podés cambiar día u horario hasta las 16:00 hs del día anterior.</div>
          )}
        </div>

        {estado.tipo === 'd1_cerrado' && (
          <a href="https://wa.me/59898610001?text=Hola%20Limpack%21%20Necesito%20cambiar%20el%20d%C3%ADa%20u%20horario%20de%20mi%20pr%C3%B3xima%20entrega.%20%C2%BFMe%20ayudan%3F" target="_blank" rel="noopener" className="mp-pe-btn mp-pe-btn-wa mp-pe-urgent">
            💬 Cambio urgente por WhatsApp
          </a>
        )}
      </div>

      <div className="mp-pe-acciones-grid">
        <button type="button" className={`mp-pe-accion ${!editableAhora ? 'mp-pe-accion-dim' : ''}`} onClick={onEditarEntrega}>
          <span className="mp-pe-accion-icon">✏️</span>
          <span className="mp-pe-accion-label">{editableAhora ? 'Día y hora' : 'Próximas'}</span>
        </button>
        <button type="button" className="mp-pe-accion" onClick={onIrDireccion}>
          <span className="mp-pe-accion-icon">📍</span>
          <span className="mp-pe-accion-label">Mi dirección</span>
        </button>
        <button type="button" className="mp-pe-accion" onClick={onSumarProductos}>
          <span className="mp-pe-accion-icon">🛒</span>
          <span className="mp-pe-accion-label">Agregar más</span>
        </button>
      </div>
    </div>
  )
}

function BloqueAccionesSuscripcion({ estado, onCambiarKit, onPausar, onReactivar, onCancelar }) {
  return (
    <div className="mp-asusc">
      <div className="mp-asusc-titulo">TU SUSCRIPCIÓN</div>
      <div className="mp-asusc-grid">
        <button type="button" className="mp-asusc-btn" onClick={onCambiarKit}>
          <span>✏️</span> Cambiar kit
        </button>
        {estado === 'pausada' ? (
          <button type="button" className="mp-asusc-btn" onClick={onReactivar}>
            <span>▶️</span> Reactivar
          </button>
        ) : (
          <button type="button" className="mp-asusc-btn" onClick={onPausar}>
            <span>⏸️</span> Pausar 1 mes
          </button>
        )}
        <button type="button" className="mp-asusc-btn mp-asusc-btn-danger" onClick={onCancelar}>
          <span>✕</span> Cancelar
        </button>
      </div>
    </div>
  )
}

function ModalCambiarDiaHorario({ susc, estadoEntrega, onCerrar, onGuardado }) {
  const [dia, setDia] = useState(susc.dia_entrega || 'lunes')
  const [ventana, setVentana] = useState(susc.ventana_horaria || 'manana')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  const noTocaActual = estadoEntrega.tipo === 'd1_cerrado'

  const diasBarrio = (() => {
    const raw = susc.barrio_obj?.dias
    if (!raw) return null
    if (Array.isArray(raw)) return raw.map(s => String(s).toLowerCase())
    return String(raw).replace(/[{}"]/g, '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
  })()
  const diasDisponibles = diasBarrio && diasBarrio.length > 0
    ? DIAS_SEMANA.filter(d => diasBarrio.includes(d.v))
    : DIAS_SEMANA

  async function guardar() {
    setError(null)
    setGuardando(true)
    try {
      await cambiarDiaSuscripcion(susc.id, dia, ventana)
      onGuardado()
    } catch (e) {
      setError(e.message || 'No pudimos guardar el cambio. Probá de nuevo.')
      setGuardando(false)
    }
  }

  return (
    <div className="mp-modal-overlay" onClick={onCerrar}>
      <div className="mp-modal mp-modal-entrega" onClick={e => e.stopPropagation()}>
        <header className="mp-modal-head">
          <div>
            <h2>Cambiar día y horario</h2>
            <p>Elegí cuándo te conviene recibir tu kit.</p>
          </div>
          <button type="button" className="mp-modal-close" onClick={onCerrar} aria-label="Cerrar">✕</button>
        </header>

        <div className="mp-modal-body">
          {noTocaActual && (
            <div className="mp-pe-banner mp-pe-banner-info" style={{ marginBottom: 16 }}>
              <span aria-hidden="true">ℹ️</span>
              <span>Tu entrega más cercana ya está armada — este cambio aplica desde la <strong>próxima</strong>.</span>
            </div>
          )}

          <label className="mp-field">
            <span>Día de la semana {susc.barrio && <em className="mp-field-hint">· días que pasamos por {susc.barrio}</em>}</span>
            <select value={dia} onChange={e => setDia(e.target.value)}>
              {diasDisponibles.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
              {/* Si el día actual no está en la lista (caso raro), lo incluimos para no perderlo */}
              {dia && !diasDisponibles.some(d => d.v === dia) && (
                <option value={dia}>{DIAS_SEMANA.find(d => d.v === dia)?.l || dia} (día actual)</option>
              )}
            </select>
            {diasDisponibles.length === 1 && (
              <em className="mp-field-hint" style={{ marginTop: 6 }}>Tu barrio solo tiene un día de reparto — si querés cambiar, escribinos por WhatsApp.</em>
            )}
          </label>

          <div className="mp-field">
            <span>Ventana horaria</span>
            <div className="mp-vent-grid">
              {VENTANAS_HORARIAS.map(v => (
                <button
                  type="button"
                  key={v.key}
                  className={`mp-vent-opt ${ventana === v.key ? 'is-active' : ''}`}
                  onClick={() => setVentana(v.key)}
                >
                  <span className="mp-vent-emoji">{v.emoji}</span>
                  <span className="mp-vent-label">{v.label}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <div className="mp-modal-error">{error}</div>}
        </div>

        <footer className="mp-modal-foot">
          <button type="button" className="mp-modal-btn-secondary" onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button type="button" className="mp-modal-btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </footer>
      </div>
    </div>
  )
}
